import * as React from 'react';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { useBoundingRect } from '../../../hooks/useBoundingRect';
import { useNewRecent } from '../../../sic-lib/api/hooks/useNewRecent';
import { INBOX_DEFAULT_PAGE_SIZE } from '../../../sic-lib/defaults';
import { InboxFilterOptions } from '../../../sic-lib/types/InboxTypes';
import PageStatus from '../../Navigation/PageStatus/PageStatus';
import NewRecentItem from '../NewRecentItem/NewRecentItem';
import NewRecentResultsPlaceholder from '../NewRecentResultsPlaceholder/NewRecentResultsPlaceholder';

interface Props {
    currentFilterOptions: InboxFilterOptions;
}

export default function NewRecentList({ currentFilterOptions }: Props) {
    // console.log("NewRecentList"); //component re-renders 3 times; 4 total renders
    const listRef = React.useRef<HTMLDivElement | null>(null);
    const infiniteLoaderRef = React.useRef<InfiniteLoader | null>(null);

    const { data, fetchNextPage, hasNextPage, isError, isFetching, isLoading } =
        useNewRecent(currentFilterOptions);

    const listRect = useBoundingRect(listRef);

    const messageList = React.useMemo(() => {
        if (!data) return [];

        //in case user filter result is empty
        if (data.pages.length > 0 && data.pages[0].items == null) {
            return [];
        }
        return data.pages.map(page => page.items).flat() || [];
    }, [data]);

    const isItemLoaded = (index: number): boolean => {
        if (data) {
            const page = (data.pageParams.length - 1) as number;
            const loaded = index < page * INBOX_DEFAULT_PAGE_SIZE;
            return loaded;
        }
        return false;
    };

    const loadMoreItems = (startIndex: number, stopIndex: number) => {
        // console.log("loadMoreItems", 'hasNextPage', hasNextPage, "data", data, "startIndex", startIndex, "stopIndex", stopIndex, "inboxData.length", messageList.length);
        if (hasNextPage && !isFetching && stopIndex + 1 >= messageList.length) {
            fetchNextPage();
        }
    };

    React.useEffect(() => {
        if (infiniteLoaderRef.current) {
            console.log('infiniteLoaderRef.current.resetloadMoreItemsCache()');
            infiniteLoaderRef.current.resetloadMoreItemsCache();
        }
    }, [currentFilterOptions, infiniteLoaderRef]);

    if (messageList.length === 0 && !isLoading) {
        return <NewRecentResultsPlaceholder />;
    }

    return (
        <div className="scrollWrapper--hideScrollbar--virtualList" ref={listRef}>
            <PageStatus showError={isError} showLoading={isLoading && messageList.length === 0}>
                <InfiniteLoader
                    itemCount={messageList.length}
                    isItemLoaded={isItemLoaded}
                    loadMoreItems={loadMoreItems}
                    minimumBatchSize={INBOX_DEFAULT_PAGE_SIZE}
                    ref={infiniteLoaderRef}
                >
                    {({ onItemsRendered, ref }) => (
                        <FixedSizeList
                            height={listRect?.height || 0}
                            width="100%"
                            itemSize={72}
                            ref={ref}
                            itemCount={messageList.length}
                            itemData={messageList}
                            onItemsRendered={onItemsRendered}
                        >
                            {({ data, index, style }) => (
                                <div style={style}>
                                    <NewRecentItem
                                        contactId={data[index].contactId}
                                        contactName={`${data[index].firstName || ''} ${
                                            data[index].salutation
                                                ? `"${data[index].salutation}"`
                                                : ''
                                        } ${data[index].lastName || ''}`.trim()}
                                        textPreview={data[index].text}
                                        numUnread={data[index].unreadCount}
                                        channelType={data[index].subType}
                                        direction={data[index].direction}
                                        lastMessage={data[index].lastDate}
                                    />
                                </div>
                            )}
                        </FixedSizeList>
                    )}
                </InfiniteLoader>
            </PageStatus>
        </div>
    );
}
